import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippy.js/react';
import { debounce } from 'lodash';
import { SearchBox, LoadingStatusWrapper, GeneralSelect, GeneralButton, Checkbox, GeneralInput } from 'sharedComponents';
import {
    IconBroadcast,
    IconInfo,
    IconGatewayFilter,
    IconMeatBallMenu,
    IconCancel,
    IconRefresh
} from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';
import { apiCallStatus } from 'helpers/constants';
import { removeUnallowedCharsFromString } from 'helpers/validate';
import useGatewayUserCapability from 'sharedHooks/useGatewayUserCapability';
import useUserCapability from 'sharedHooks/useUserCapability';
import { getAssetIcon } from 'helpers/icons';

import GatewayListManagement from './GatewayListManagementComponent';
import { GatewayDetails } from './GatewayDetails';
import { SerialNumberEditor } from './SerialNumberEditor';
import { filteredGatewayFromGatewayList, formatPropertyKeyForId, getMappedDriveApplications, MAX_SEARCH_LENGTH } from './helpers';
import { ASSET_DETAILS_LABEL, summaryConnectionStatusValues, GATEWAY_MAIN_TABS, GATEWAY_SUB_TABS, GATEWAY_MAIN_TYPES, GATEWAY_SUB_TYPES, ASSET_NAME_MAX_LENGTH, ARROW_RIGHT } from './constants';
import GatewayManagementEmptyComponent from './GatewayManagementEmptyComponent';

const GatewayManagmentComponent = ({ model, actions }) => {
    const { t: translate } = useTranslation();
    const userGatewayCapability = useGatewayUserCapability();
    const userCapability = useUserCapability();
    const hasGatewayAdminViewCapability = userCapability?.hasGatewayAdminViewCapability?.() === true;
    const hasGatewayAdminReadViewCapability = userCapability?.hasGatewayAdminReadView?.() === true;
    const isGatewayReadOnlyMode = hasGatewayAdminReadViewCapability && !hasGatewayAdminViewCapability;
    const [leftWidth, setLeftWidth] = useState(32);
    const [rightWidth, setRightWidth] = useState(0);
    const [dragTarget, setDragTarget] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedGatewayType, setSelectedGatewayType] = useState(null);
    const [selectedDriveApplication, setSelectedDriveApplication] = useState(null);
    const { selectedOrganization, gatewayDetails, gatewayType, saveAssetDriveApplicationLoadingStatus, gatewayAssets, assignedSerialNumberLoadingStatus } = model;
    const gatewayAssetsLoadingStatus = gatewayAssets?.loadingStatus;
    const {
        getGatewayList,
        getGatewayType,
        selectAsset,
        openSerialNumberEditorModal,
        closeSerialNumberEditorModal,
        getUpdateAssetDriveApplication,
        getDriveApplications,
        setSelectedGateway
    } = actions || {};
    const [gatewayID, setGatewayID] = useState(null);
    const [gatewayStatus, setGatewayStatus] = useState(null);
    const [statusChangeTimestamp, setStatusChangeTimestamp] = useState(null);
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [enableRightPanel, setEnableRightPanel] = useState(false);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [isConnectedChecked, setIsConnectedChecked] = useState(false);
    const [isDisconnectedChecked, setIsDisconnectedChecked] = useState(false);
    const [isConnectedWithWarningChecked, setIsConnectedWithWarningChecked] = useState(false);
    const [isDriveApplicationChanged, setIsDriveApplicationChanged] = useState(false);
    const { isModalOpen, driveApplications } = model;
    const gatewayExists = gatewayDetails?.gatewayList?.some(gateway => gateway?.gatewayIdentifier === gatewayID) ||
        gatewayDetails?.otherGateways?.unknownGateways?.gatewayIdentifier === gatewayID;
    const [isShowGatewayDetails, setIsShowGatewayDetails] = useState(false);
    const mappedDriveApplications = getMappedDriveApplications(driveApplications);
    const [isSearchPerformed, setIsSearchPerformed] = useState(false);
    const [editedAssetName, setEditedAssetName] = useState('');
    const [isAssetNameChanged, setIsAssetNameChanged] = useState(false);
    const [assetNameError, setAssetNameError] = useState('');
    const [isShowAssetDetails, setIsShowAssetDetails] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [activeMainTab, setActiveMainTab] = useState(GATEWAY_MAIN_TABS.MY_GATEWAYS);
    const [activeSubTab, setActiveSubTab] = useState(GATEWAY_SUB_TABS.EMPTY_GATEWAYS);
    const hasUnsavedChanges = isDriveApplicationChanged || isAssetNameChanged;
    const gatewayManagementRef = useRef(null);

    const isNoGatewaySelected = gatewayID === null;
    const canEditAsset = useMemo(
        () => isNoGatewaySelected || userGatewayCapability.hasDriveAdminCapability(),
        [isNoGatewaySelected, userGatewayCapability]
    );

    useEffect(() => {
        if (!isDriveApplicationChanged && selectedDetails && !isAssetNameChanged) {
            const application = selectedDetails.assetProperties?.find(
                item => item?.propertyKey === ASSET_DETAILS_LABEL.APPLICATION
            );
            const filteredApplication = application
                ? mappedDriveApplications.find(driveApp => driveApp?.value === application?.propertyValue)
                : null;
            setSelectedDriveApplication(filteredApplication || null);

            // Only set the initial asset name when NOT editing
            setEditedAssetName(selectedDetails?.name || '');
        }
    }, [selectedDetails, isDriveApplicationChanged, mappedDriveApplications, isAssetNameChanged]);

    useEffect(() => {
        // Clear isSaving only when save is successful AND assets have been refetched
        if (isSaving &&
            saveAssetDriveApplicationLoadingStatus === apiCallStatus.SUCCESS &&
            gatewayAssetsLoadingStatus === apiCallStatus.SUCCESS) {
            setIsSaving(false);
        }
    }, [isSaving, saveAssetDriveApplicationLoadingStatus, gatewayAssetsLoadingStatus]);

    const getActiveGatewayList = useCallback(() => {
        if (activeMainTab === GATEWAY_MAIN_TABS.MY_GATEWAYS) {
            return gatewayDetails?.myGateways || [];
        }
        if (activeMainTab === GATEWAY_MAIN_TABS.OTHER_GATEWAYS) {
            if (activeSubTab === GATEWAY_SUB_TABS.EMPTY_GATEWAYS) {
                return gatewayDetails?.otherGateways?.emptyGateways || [];
            }
            if (activeSubTab === GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS) {
                return gatewayDetails?.otherGateways?.unassignedGateways || [];
            }
        }
        return [];
    }, [activeMainTab, activeSubTab, gatewayDetails]);

    const resetGatewaySelection = () => {
        setSearchValue('');
        setSearchError('');
        setSelectedGatewayType(null);
        setIsConnectedChecked(false);
        setIsDisconnectedChecked(false);
        setIsConnectedWithWarningChecked(false);
        setSelectedDriveApplication(null);
        setEnableRightPanel(false);
        setIsShowGatewayDetails(false);
        setIsSearchPerformed(false);
        setLeftWidth(32);
        setRightWidth(0);
        setActiveMainTab(GATEWAY_MAIN_TABS.MY_GATEWAYS);
        setActiveSubTab(GATEWAY_SUB_TABS.EMPTY_GATEWAYS);
    };

    const getGatewayFilterPayload = useCallback(() => {
        if (activeMainTab === GATEWAY_MAIN_TABS.MY_GATEWAYS) {
            return {
                type: GATEWAY_MAIN_TYPES.MY_GATEWAYS,
                subType: GATEWAY_SUB_TYPES.NONE
            };
        } else {
            // OTHER_GATEWAYS
            let subType = GATEWAY_SUB_TYPES.NONE;
            if (activeSubTab === GATEWAY_SUB_TABS.EMPTY_GATEWAYS) {
                subType = GATEWAY_SUB_TYPES.EMPTY;
            } else if (activeSubTab === GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS) {
                subType = GATEWAY_SUB_TYPES.UNKNOWN;
            }
            return {
                type: GATEWAY_MAIN_TYPES.OTHER_GATEWAYS,
                subType
            };
        }
    }, [activeMainTab, activeSubTab]);

    const handleResetFilter = () => {
        // Reset only filter-related state, preserve current tab/sub-tab
        setSearchValue('');
        setSearchError('');
        setSelectedGatewayType(null);
        setIsConnectedChecked(false);
        setIsDisconnectedChecked(false);
        setIsConnectedWithWarningChecked(false);
        setIsFilterActive(false);
        setIsSearchPerformed(false);

        // Fetch list with current tab filter but no search filters
        if (selectedOrganization?.organizationID) {
            const gatewayFilter = getGatewayFilterPayload();
            getGatewayList(selectedOrganization.organizationID, [], null, gatewayFilter);
        }
    };

    useEffect(() => {
        if (selectedOrganization?.organizationID) {
            // Reset tabs and fetch with My Gateways filter when organization changes
            resetGatewaySelection();
            const gatewayFilter = {
                type: GATEWAY_MAIN_TYPES.MY_GATEWAYS,
                subType: GATEWAY_SUB_TYPES.NONE
            };
            getGatewayList(selectedOrganization?.organizationID, [], null, gatewayFilter);
            getGatewayType();
            setGatewayID(null);
            setGatewayStatus(null);
            getDriveApplications();
            setStatusChangeTimestamp(null);
        }
    }, [selectedOrganization?.organizationID, getGatewayList, getGatewayType, getDriveApplications]);

    const selectedAsset = useMemo(() => {
        if (!selectedDetails?.assetId || !gatewayAssets?.gatewayConnectedAssets?.gatewayAssets) {
            return null;
        }
        return gatewayAssets.gatewayConnectedAssets.gatewayAssets.find(
            asset => asset.assetId === selectedDetails.assetId
        );
    }, [selectedDetails?.assetId, gatewayAssets?.gatewayConnectedAssets?.gatewayAssets]);

    useEffect(() => {
        // Don't update if user has unsaved changes or if save/assign is in progress
        if (saveAssetDriveApplicationLoadingStatus === apiCallStatus.LOADING ||
            assignedSerialNumberLoadingStatus === apiCallStatus.LOADING ||
            isAssetNameChanged ||
            isDriveApplicationChanged ||
            isSaving) {
            return;
        }

        if (!selectedDetails?.assetId) {
            return;
        }

        if (selectedAsset) {
            const updatedSelectedDetails = {
                ...selectedAsset,
                originalSerialNumber: selectedDetails?.originalSerialNumber || selectedAsset?.serialNumber
            };
            setSelectedDetails(updatedSelectedDetails);
            // Update the input field with the server's actual value
            setEditedAssetName(selectedAsset?.name || '');
        }
    }, [selectedAsset, saveAssetDriveApplicationLoadingStatus, assignedSerialNumberLoadingStatus, selectedDetails?.assetId, isAssetNameChanged, isDriveApplicationChanged, isSaving]);

    const handleMouseDown = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleMouseDownLeft = () => {
        setDragTarget('left');
        handleMouseDown();
    };

    const handleMouseDownRight = () => {
        setDragTarget('right');
        handleMouseDown();
    };

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setDragTarget(null);
    }, []);

    const handleMouseMove = useCallback(
        (e) => {
            if (!isDragging || !dragTarget) { return; }

            const containerRect = gatewayManagementRef.current?.getBoundingClientRect();
            if (!containerRect) { return; }
            const mouseXPercent =
                (e.clientX - containerRect.left) / containerRect.width * 100;

            if (dragTarget === 'left') {
                // Restrict movement when right panel is active to avoid clash
                const maxLeft = enableRightPanel ? 100 - rightWidth - 50 : 50;
                const newLeftWidth = Math.min(Math.max(mouseXPercent, 20), maxLeft);
                setLeftWidth(newLeftWidth);
            }

            if (dragTarget === 'right' && enableRightPanel) {
                // Restrict movement when left is already taking space
                const maxRight = 100 - leftWidth - 50;
                const newRightWidth = Math.min(Math.max(100 - mouseXPercent, 20), maxRight);
                setRightWidth(newRightWidth);
            }
        },
        [isDragging, dragTarget, enableRightPanel, leftWidth, rightWidth]
    );

    useEffect(() => {
        if (!isDragging) {
            return undefined;
        }

        globalThis.addEventListener('mousemove', handleMouseMove);
        globalThis.addEventListener('mouseup', handleMouseUp);

        return () => {
            globalThis.removeEventListener('mousemove', handleMouseMove);
            globalThis.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const toggleFilter = () => {
        setIsFilterActive(prevState => !prevState);
    };

    const executeSearch = useCallback((searchText) => {
        setEnableRightPanel(false);
        setIsShowGatewayDetails(false);
        setLeftWidth(32);
        setRightWidth(0);
        const summaryConnectionStatus = [];
        if (isConnectedChecked) { summaryConnectionStatus.push(summaryConnectionStatusValues.CONNECTED); }
        if (isConnectedWithWarningChecked) { summaryConnectionStatus.push(summaryConnectionStatusValues.CONNECTED_WITH_WARNING); }
        if (isDisconnectedChecked) { summaryConnectionStatus.push(summaryConnectionStatusValues.DISCONNECTED); }

        const filters = {
            keyword: searchText,
            gatewayType: selectedGatewayType?.gatewayType || null,
            summaryConnectionStatus
        };

        const siteIDArr = [];
        for (const item of gatewayDetails.gatewayList) {
            const siteId = item?.site?.siteId;
            if (siteId && !siteIDArr.includes(siteId)) {
                siteIDArr.push(siteId);
            }
        }

        if (selectedOrganization?.organizationID) {
            const gatewayFilter = getGatewayFilterPayload();
            getGatewayList(selectedOrganization.organizationID, siteIDArr, filters, gatewayFilter);
        }
    }, [isConnectedChecked, isConnectedWithWarningChecked, isDisconnectedChecked, selectedGatewayType, gatewayDetails.gatewayList, selectedOrganization, getGatewayList, getGatewayFilterPayload]);

    const debouncedSearch = useMemo(
        () => debounce((value) => executeSearch(value), 500),
        [executeSearch]
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const getSearchErrorMessage = (value) => {
        if (value && value.length > MAX_SEARCH_LENGTH) {
            return translate('ABB.Powertrain.Frontend.searchLengthValidation');
        }
        return '';
    };

    const handleSearch = () => {
        executeSearch(searchValue);
        setIsSearchPerformed(false);
    };

    const handleFilterSearchChange = (value) => {
        setSearchValue(value);
        const errorMsg = getSearchErrorMessage(value);
        setSearchError(errorMsg);

        if (value.length <= MAX_SEARCH_LENGTH) {
            debouncedSearch(value);
        }
    };

    const getCurrentFilters = useCallback(() => {
        const summaryConnectionStatus = [];
        if (isConnectedChecked) { summaryConnectionStatus.push(summaryConnectionStatusValues.CONNECTED); }
        if (isConnectedWithWarningChecked) { summaryConnectionStatus.push(summaryConnectionStatusValues.CONNECTED_WITH_WARNING); }
        if (isDisconnectedChecked) { summaryConnectionStatus.push(summaryConnectionStatusValues.DISCONNECTED); }

        const hasFilters = searchValue || selectedGatewayType || summaryConnectionStatus.length > 0;

        return hasFilters ? {
            keyword: searchValue,
            gatewayType: selectedGatewayType?.gatewayType || null,
            summaryConnectionStatus
        } : null;
    }, [searchValue, selectedGatewayType, isConnectedChecked, isConnectedWithWarningChecked, isDisconnectedChecked]);

    const getCurrentSiteIds = useCallback(() => {
        const siteIDArr = [];
        for (const item of gatewayDetails?.gatewayList || []) {
            const siteId = item?.site?.siteId;
            if (siteId && !siteIDArr.includes(siteId)) {
                siteIDArr.push(siteId);
            }
        }
        return siteIDArr;
    }, [gatewayDetails?.gatewayList]);

    const getAssetNameErrorMessage = (value) => {
        if (!value || value.trim().length === 0) {
            return translate('ABB.Powertrain.Frontend.assetInfoassetNameRequired');
        }
        if (value.trim().length > ASSET_NAME_MAX_LENGTH) {
            return translate('ABB.Powertrain.Frontend.assetInfoassetNameValidation');
        }
        return '';
    };

    const handleResetAssetInfo = () => {
        setLeftWidth(32);
        setRightWidth(0);
        setEnableRightPanel(false);
        setIsDriveApplicationChanged(false);
        setIsAssetNameChanged(false);
        setEditedAssetName('');
        setAssetNameError('');
        setSelectedDriveApplication(null);
        setIsShowAssetDetails(false);
        selectAsset(null);

        if (isDriveApplicationChanged && selectedDetails?.originalSerialNumber) {
            const resetDetails = {
                ...selectedDetails,
                serialNumber: selectedDetails.originalSerialNumber
            };
            setSelectedDetails(resetDetails);
        }
    };

    const handleMainTabChange = (tab) => {
        setActiveMainTab(tab);
        setGatewayID(null);
        setGatewayStatus(null);
        setStatusChangeTimestamp(null);
        setIsShowGatewayDetails(false);
        handleResetAssetInfo();

        // Reset all filters when switching main tabs
        setSearchValue('');
        setSearchError('');
        setSelectedGatewayType(null);
        setIsConnectedChecked(false);
        setIsDisconnectedChecked(false);
        setIsConnectedWithWarningChecked(false);
        setIsFilterActive(false);
        setIsSearchPerformed(false);

        // Refresh gateway list with new filter (no search filters applied)
        if (selectedOrganization?.organizationID) {
            const siteIds = getCurrentSiteIds();
            const gatewayFilter = tab === GATEWAY_MAIN_TABS.MY_GATEWAYS
                ? { type: GATEWAY_MAIN_TYPES.MY_GATEWAYS, subType: GATEWAY_SUB_TYPES.NONE }
                : {
                    type: GATEWAY_MAIN_TYPES.OTHER_GATEWAYS,
                    subType: activeSubTab === GATEWAY_SUB_TABS.EMPTY_GATEWAYS ? GATEWAY_SUB_TYPES.EMPTY
                        : activeSubTab === GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS ? GATEWAY_SUB_TYPES.UNKNOWN
                            : GATEWAY_SUB_TYPES.NONE
                };
            getGatewayList(selectedOrganization.organizationID, siteIds, null, gatewayFilter);
        }
    };

    const handleSubTabChange = (tab) => {
        setActiveSubTab(tab);
        setGatewayID(null);
        setGatewayStatus(null);
        setStatusChangeTimestamp(null);
        setIsShowGatewayDetails(false);
        handleResetAssetInfo();

        // Reset all filters when switching sub-tabs
        setSearchValue('');
        setSearchError('');
        setSelectedGatewayType(null);
        setIsConnectedChecked(false);
        setIsDisconnectedChecked(false);
        setIsConnectedWithWarningChecked(false);
        setIsFilterActive(false);
        setIsSearchPerformed(false);

        // Refresh gateway list with new sub-filter (no search filters applied)
        if (selectedOrganization?.organizationID) {
            const siteIds = getCurrentSiteIds();
            const subTypeMap = {
                [GATEWAY_SUB_TABS.EMPTY_GATEWAYS]: GATEWAY_SUB_TYPES.EMPTY,
                [GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS]: GATEWAY_SUB_TYPES.UNKNOWN
            };
            const gatewayFilter = {
                type: GATEWAY_MAIN_TYPES.OTHER_GATEWAYS,
                subType: subTypeMap[tab] || GATEWAY_SUB_TYPES.NONE
            };
            getGatewayList(selectedOrganization.organizationID, siteIds, null, gatewayFilter);
        }
    };

    const handleShowInfoClick = (details) => {
        // collapse asset side panel and reset
        if (isShowAssetDetails && selectedDetails?.assetId === details?.assetId) {
            handleResetAssetInfo();
            setIsShowAssetDetails(false);
            setSelectedDetails(null);
            setEnableRightPanel(false);
            selectAsset(null);
            return;
        }
        if (details) {
            const detailsWithOriginalSerialNumber = {
                ...details,
                originalSerialNumber: details.originalSerialNumber || details.serialNumber
            };
            setSelectedDetails(detailsWithOriginalSerialNumber);
            const initialAssetName = details?.name || '';
            setEditedAssetName(initialAssetName);
            setIsAssetNameChanged(false);
            const initialAssetNameError = getAssetNameErrorMessage(initialAssetName);
            setAssetNameError(initialAssetNameError);
            setLeftWidth(32);
            setRightWidth(23);
            setEnableRightPanel(true);
            selectAsset(details);
            setIsShowAssetDetails(true);
        }
    };

    const handleAssetNameChange = (value) => {
        const hasSpecialCharsInName = removeUnallowedCharsFromString(value) !== value;
        const correctedAssetName = hasSpecialCharsInName ? removeUnallowedCharsFromString(value) ?? value : value;

        setEditedAssetName(correctedAssetName);
        // Compare actual values, not trimmed, to preserve spaces while editing
        const originalName = selectedDetails?.name || '';
        setIsAssetNameChanged(correctedAssetName !== originalName);

        if (hasSpecialCharsInName) {
            setAssetNameError(translate('ABB.Powertrain.Frontend.assetInfoassetNameSpecialChars'));
        } else {
            setAssetNameError(getAssetNameErrorMessage(correctedAssetName));
        }
    };

    const validateAssetName = (value) => {
        if (!value || value.trim().length === 0) {
            return false;
        }
        return value.trim().length <= ASSET_NAME_MAX_LENGTH;
    };

    const handleOnSelectedGatewayData = ({ id, status, timeStamp }) => {
        setGatewayID(id);
        setGatewayStatus(status);
        setStatusChangeTimestamp(timeStamp);
        setIsShowGatewayDetails(true);
        handleResetAssetInfo();

        // Check if this is the unknown gateway
        const unknownGateway = gatewayDetails?.otherGateways?.unknownGateways;
        if (unknownGateway && id === unknownGateway.gatewayIdentifier) {
            setSelectedGateway(unknownGateway);
        } else {
            const filteredGateway = filteredGatewayFromGatewayList(gatewayDetails?.gatewayList, id);
            setSelectedGateway(filteredGateway);
        }
    };

    const handleOpenSerialNumberEditorModal = () => {
        openSerialNumberEditorModal();
    };

    const handleCloseSerialNumberEditorModal = () => {
        closeSerialNumberEditorModal();
    };

    const handleApplySerialNumber = (data) => {
        if (selectedDetails && data.currentSerialNumber) {
            let serialNumber = data.currentSerialNumber;
            if (
                data.IsCabinetDriveValid &&
                selectedDetails.isMultiModuleDrive &&
                data.suffix &&
                !serialNumber.endsWith(`_${data.suffix}`)
            ) {
                serialNumber = `${serialNumber}_${data.suffix}`;
            }

            let cabinetSerialNumber = data.currentSerialNumber;
            if (
                (selectedDetails.isMultiModuleDrive || data.IsCabinetDriveValid) &&
                data.suffix &&
                cabinetSerialNumber.endsWith(`_${data.suffix}`)
            ) {
                // Remove suffix from cabinet serial number if present
                cabinetSerialNumber = cabinetSerialNumber.slice(0, -`_${data.suffix}`.length);
            }

            const updatedDetails = {
                ...selectedDetails,
                serialNumber: serialNumber,
                cabinetSerialNumber: cabinetSerialNumber,
                originalSerialNumber: data.currentSerialNumber,
                suffix: selectedDetails.isMultiModuleDrive ? data.suffix : null,
                isMultiModuleDrive: data.isMultiModuleDrive
            };

            setSelectedDetails(updatedDetails);
        }

        closeSerialNumberEditorModal();
    };

    const handleDriveApplicationChange = (app) => {
        setSelectedDriveApplication(app);
        setIsDriveApplicationChanged(true);
    };

    const handleSaveAssetDriveApplication = () => {
        // trim asset name and replace multiple spaces with single space
        const trimmedAssetName = editedAssetName ? editedAssetName.trim().replace(/\s+/g, ' ') : '';

        // Set saving flag to prevent server updates during save
        setIsSaving(true);
        // Optimistically update the UI immediately for instant feedback
        const updatedDetails = { ...selectedDetails };
        if (isAssetNameChanged) {
            updatedDetails.name = trimmedAssetName;
        }
        if (isDriveApplicationChanged && selectedDriveApplication) {
            updatedDetails.assetProperties = (selectedDetails.assetProperties || []).map(prop =>
                prop.propertyKey === ASSET_DETAILS_LABEL.APPLICATION
                    ? { ...prop, propertyValue: selectedDriveApplication.value }
                    : prop
            );
        }
        // Update state immediately
        setSelectedDetails(updatedDetails);
        setEditedAssetName(trimmedAssetName);

        setIsDriveApplicationChanged(false);
        setIsAssetNameChanged(false);

        const payload = {
            id: selectedDetails?.assetId,
            assetId: selectedDetails?.assetId,
            assetName: trimmedAssetName,
            applicationId: selectedDriveApplication?.value
        };

        // API call happens after UI update
        getUpdateAssetDriveApplication(payload, gatewayID);
    };


    const renderRightPanelContent = (connectedAssetItem) => {
        const assetPropertyItems = connectedAssetItem?.assetProperties || [];
        const modifiedGatewayInfo = [
            {
                propertyKey: translate('ABB.Powertrain.Frontend.assetNameLabel'),
                propertyName: translate('ABB.Powertrain.Frontend.assetNameLabel'),
                propertyValue: connectedAssetItem?.name || '-'
            },
            {
                propertyKey: translate('ABB.Powertrain.Frontend.serialNumberLabel'),
                propertyName: translate('ABB.Powertrain.Frontend.serialNumberLabel'),
                propertyValue: connectedAssetItem?.serialNumber || '-',
                isSyntheticSerialNumber: connectedAssetItem?.isSyntheticSerialNumber || false,
                serialNumber: connectedAssetItem?.serialNumber || '',
                cabinetSerialNumber: connectedAssetItem?.cabinetSerialNumber || ''
            },
            ...assetPropertyItems
        ];
        const pairedData = [];
        for (let i = 0; i < modifiedGatewayInfo?.length; i += 2) {
            pairedData.push(modifiedGatewayInfo?.slice(i, i + 2));
        }
        const filteredProperties = assetPropertyItems.filter(item =>
            item?.propertyKey !== ASSET_DETAILS_LABEL.ASSET_NAME &&
            item?.propertyKey !== ASSET_DETAILS_LABEL.SERIAL_NUMBER &&
            item?.propertyKey !== ASSET_DETAILS_LABEL.APPLICATION
        );

        const renderDynamicProperties = () => {
            return filteredProperties.map((item, index) =>
                //combine word propertryKey
                <div className='asset-info__row' id={`asset-info__row__${formatPropertyKeyForId(item?.propertyKey)}`} key={index}>
                    <div className='asset-info__col'>
                        <div className='asset-info__label'>
                            {item?.propertyName || item?.propertyKey || ' '}
                        </div>
                        <div className='asset-info__value text-truncate' title={item?.propertyValue}>
                            {item?.propertyValue || '-'}
                        </div>
                    </div>
                </div>
            );
        };

        const renderRow = () => {
            return (
                <>
                    <div className='asset-info__row' id='asset-info__row__assetname'>
                        <div className='asset-info__col'>
                            <div className='asset-info__label'>
                                {translate('ABB.Powertrain.Frontend.assetNameLabel') || ' '}
                            </div>

                            {canEditAsset ?
                                <>
                                    <GeneralInput
                                        id='asset-info__asset-name-input'
                                        value={editedAssetName}
                                        onChange={handleAssetNameChange}
                                        placeholder={translate('ABB.Powertrain.Frontend.assetNameLabel')}
                                        className={`asset-info__asset-name ${assetNameError ? 'asset-info__error-input' : ''}`}
                                    />
                                    {assetNameError && <div className='asset-info__error'>
                                        <IconCancel
                                            showCircle={true}
                                            color={colors.white}
                                            fillColor={colors.pomegranate}
                                            viewBox='0 0 50 50'
                                            width={18}
                                            height={18}
                                        /><span className=''>{assetNameError}</span>
                                    </div>}
                                </>
                                :
                                <div className='asset-info__value text-truncate' title={connectedAssetItem?.name || '-'}
                                >
                                    {connectedAssetItem?.name || '-'}
                                </div>
                            }

                        </div>
                    </div>
                    <div className='asset-info__row' id='asset-info__row__serialnumber'>
                        <div className='asset-info__col'>
                            <div className='asset-info__label'>
                                {translate('ABB.Powertrain.Frontend.serialNumberLabel') || ' '}
                            </div>
                            <div className={`asset-info__value ${canEditAsset ? 'asset-info__value--with-actions' : ''}`} title={connectedAssetItem?.serialNumber || '-'}>
                                <span className='asset-info__value-text'>
                                    {connectedAssetItem?.serialNumber || '-'}
                                </span>
                                {canEditAsset && !isGatewayReadOnlyMode ? <div className='asset-info__actions'>
                                    <button className='asset-info__action-btn' onClick={() => handleOpenSerialNumberEditorModal()}><IconMeatBallMenu /></button>
                                </div> : null}
                            </div>
                        </div>
                    </div>
                    {renderDynamicProperties()}
                    {canEditAsset && !connectedAssetItem?.isUnknownTypeConfig && < div className='asset-info__row' id='asset-info__row__driveapplication'>
                        <div className='asset-info__col'>
                            <div className='asset-info__label'>
                                {translate('ABB.Powertrain.Frontend.assetInfoDriveApplication') || ' '}
                            </div>
                            <div className='asset-info__applications'>
                                <GeneralSelect
                                    className='asset-info__drive-select'
                                    options={mappedDriveApplications}
                                    onChange={(app) => handleDriveApplicationChange(app)}
                                    value={selectedDriveApplication}
                                    menuShouldScrollIntoView={false}
                                    isSearchable={false}
                                    isDisabled={isGatewayReadOnlyMode}
                                />
                            </div>
                        </div>
                    </div >}
                </>
            );
        };
        return <div className='asset-info'>
            <div className='asset-info__header-title'>
                <div className='asset-info__header-title__icon'>
                    {getAssetIcon(connectedAssetItem?.assetType, { width: 18, height: 18 })}
                </div>
                <Tippy
                    content={connectedAssetItem?.name || '-'}
                    interactive={true}
                    interactiveBorder={10}
                    animation='scale'
                    theme='light-border'
                    trigger='mouseenter'
                    placement='bottom'
                >
                    <span className='title'>{connectedAssetItem?.name || '-'}</span>
                </Tippy>
            </div>
            <div className='asset-info__primary'>
                {renderRow()}
            </div>
            <div className='asset-info__secondary'>
                <div className='asset-info__row' id='asset-info__row__organization-name'>
                    <div className='asset-info__col'>
                        <div className='asset-info__label'>{translate('ABB.Powertrain.Frontend.assetInfoOperator')}</div>
                        <div className='asset-info__value text-truncate' title={connectedAssetItem?.organization?.name}>{connectedAssetItem?.organization?.name || '-'}</div>
                    </div>
                </div>
                <div className='asset-info__row' id='asset-info__row__country-name'>
                    <div className='asset-info__col'>
                        <div className='asset-info__label'>{translate('ABB.Powertrain.Frontend.countryNameLabel')}</div>
                        <div className='asset-info__value text-truncate' title={connectedAssetItem?.site?.country}>{connectedAssetItem?.site?.country || '-'}</div>
                    </div>
                </div>
                <div className='asset-info__row'>
                    <div className='asset-info__col' id='asset-info__row__site-name'>
                        <div className='asset-info__label'>{translate('ABB.Powertrain.Frontend.assetInfoCustomer')}</div>
                        <div className='asset-info__value text-truncate' title={connectedAssetItem?.site?.siteName}>{connectedAssetItem?.site?.siteName || '-'}</div>
                    </div>
                </div>
                <div className='asset-info__row' id='asset-info__row__site-id'>
                    <div className='asset-info__col'>
                        <div className='asset-info__label'>{translate('ABB.Powertrain.Frontend.assetInfoSiteID')}</div>
                        <div className='asset-info__value text-truncate' title={connectedAssetItem?.site?.siteId}>{connectedAssetItem?.site?.siteId || '-'}</div>
                    </div>
                </div>
            </div>
        </div>;
    };

    const tippyOtherGatewaysInfo = () => {
        return <div className='tippy-info-content'>
            <p>{translate('ABB.Powertrain.Frontend.gatewayManagementOtherGatewaysInfo')}</p>
            <ul>
                <li><b>{translate('ABB.Powertrain.Frontend.gatewayManagementEmptyGateways')}</b> {translate('ABB.Powertrain.Frontend.gatewayManagementEmptyGatewaysInfo')}</li>
                <li><b>{translate('ABB.Powertrain.Frontend.gatewayManagementUnknownGateways')}</b> {translate('ABB.Powertrain.Frontend.gatewayManagementUnknownGatewaysInfo')}</li>
            </ul>
        </div>;
    };

    // Extracted to keep the main component's cognitive complexity low (SonarCloud)
    const getTabPanelAttributes = () => {
        if (activeMainTab === GATEWAY_MAIN_TABS.MY_GATEWAYS) {
            return { id: 'tabpanel-my-gateways', ariaLabelledBy: 'tab-my-gateways' };
        }
        if (activeSubTab === GATEWAY_SUB_TABS.EMPTY_GATEWAYS) {
            return { id: 'tabpanel-empty-gateways', ariaLabelledBy: 'tab-empty-gateways' };
        }
        return { id: 'tabpanel-unknown-gateways', ariaLabelledBy: 'tab-unknown-gateways' };
    };

    const renderOtherGatewaysTabButton = () => {
        if (isGatewayReadOnlyMode) {
            return null;
        }
        return (
            <button
                className={`gateway-tab ${activeMainTab === GATEWAY_MAIN_TABS.OTHER_GATEWAYS ? 'gateway-tab--active' : ''}`}
                role='tab'
                aria-selected={activeMainTab === GATEWAY_MAIN_TABS.OTHER_GATEWAYS}
                aria-controls='tabpanel-other-gateways'
                id='tab-other-gateways'
                tabIndex={activeMainTab === GATEWAY_MAIN_TABS.OTHER_GATEWAYS ? 0 : -1}
                onClick={() => handleMainTabChange(GATEWAY_MAIN_TABS.OTHER_GATEWAYS)}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft') {
                        handleMainTabChange(GATEWAY_MAIN_TABS.MY_GATEWAYS);
                    }
                }}
            >
                <span className='gateway-tab__label'>
                    {translate('ABB.Powertrain.Frontend.gatewayManagementOtherGateways')}
                </span>
                <Tippy
                    content={tippyOtherGatewaysInfo()}
                    interactive={true}
                    interactiveBorder={10}
                    animation='scale'
                    theme='light-border'
                    trigger='click'
                    placement='bottom'
                >
                    <button
                        type='button'
                        className='gateway-tab__info'
                        aria-label={translate('ABB.Powertrain.Frontend.gatewayManagementOtherGatewaysInfo')}
                        onClick={(e) => { e.stopPropagation(); }}
                    >
                        <IconInfo width={'16'} height={'16'} color={colors.mineShaft} />
                    </button>
                </Tippy>
            </button>
        );
    };

    const renderGatewaySubTabs = () => {
        if (isGatewayReadOnlyMode || activeMainTab !== GATEWAY_MAIN_TABS.OTHER_GATEWAYS) {
            return null;
        }
        return (
            <div
                className='gateway-subtabs'
                role='tablist'
                aria-label={translate('ABB.Powertrain.Frontend.gatewayManagementOtherGateways')}
            >
                <button
                    className={`gateway-subtab ${activeSubTab === GATEWAY_SUB_TABS.EMPTY_GATEWAYS ? 'gateway-subtab--active' : ''}`}
                    role='tab'
                    aria-selected={activeSubTab === GATEWAY_SUB_TABS.EMPTY_GATEWAYS}
                    aria-controls='tabpanel-empty-gateways'
                    id='tab-empty-gateways'
                    tabIndex={activeSubTab === GATEWAY_SUB_TABS.EMPTY_GATEWAYS ? 0 : -1}
                    onClick={() => handleSubTabChange(GATEWAY_SUB_TABS.EMPTY_GATEWAYS)}
                    onKeyDown={(e) => {
                        if (e.key === ARROW_RIGHT) {
                            handleSubTabChange(GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS);
                        }
                    }}
                >
                    {translate('ABB.Powertrain.Frontend.gatewayManagementEmptyGateways')}
                </button>
                <button
                    className={`gateway-subtab ${activeSubTab === GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS ? 'gateway-subtab--active' : ''}`}
                    role='tab'
                    aria-selected={activeSubTab === GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS}
                    aria-controls='tabpanel-unknown-gateways'
                    id='tab-unknown-gateways'
                    tabIndex={activeSubTab === GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS ? 0 : -1}
                    onClick={() => handleSubTabChange(GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS)}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft') {
                            handleSubTabChange(GATEWAY_SUB_TABS.EMPTY_GATEWAYS);
                        }
                    }}
                >
                    {translate('ABB.Powertrain.Frontend.gatewayManagementUnknownGateways')}
                </button>
            </div>
        );
    };

    const renderUnknownGatewaySection = () => {
        if (isGatewayReadOnlyMode ||
            activeMainTab !== GATEWAY_MAIN_TABS.OTHER_GATEWAYS ||
            activeSubTab !== GATEWAY_SUB_TABS.UNKNOWN_GATEWAYS ||
            !gatewayDetails?.otherGateways?.unknownGateways) {
            return null;
        }
        const unknownGateway = gatewayDetails.otherGateways.unknownGateways;
        const isActiveUnknownGateway = gatewayID === unknownGateway.gatewayIdentifier && isShowGatewayDetails;
        return (
            <div className='unknown-gateway-section'>
                <button
                    className={`unknown-gateway-card ${isActiveUnknownGateway ? 'unknown-gateway-card--active' : ''}`}
                    onClick={() => handleOnSelectedGatewayData({
                        id: unknownGateway.gatewayIdentifier,
                        status: unknownGateway.connectivityStatus,
                        timeStamp: unknownGateway.statusChangeTimestamp
                    })}
                    type='button'>
                    <div className='unknown-gateway-card__content'>
                        <div className='unknown-gateway-card__header'>
                            <span className='unknown-gateway-card__title'>
                                {translate('ABB.Powertrain.Frontend.gatewayManagementUnknownGatewayTitle')}
                            </span>
                        </div>
                        <div className='unknown-gateway-card__description'>
                            {translate('ABB.Powertrain.Frontend.gatewayManagementUnknownGatewayDescription')}
                        </div>
                    </div>
                </button>
                <div className='gateway-section-divider'></div>
                <div className='gateway-section-header'>
                    {translate('ABB.Powertrain.Frontend.gatewayManagementGatewaysWithUnassignedAssets')}
                </div>
            </div>
        );
    };

    const { id: tabPanelId, ariaLabelledBy: tabPanelAriaLabelledBy } = getTabPanelAttributes();

    return (
        <div className={`gateway-management-wrapper ${enableRightPanel ? 'enabled-right-panel' : ''}`}>
            <div className='gateway-management' ref={gatewayManagementRef}>
                <div className='gateway-management__left-pane' style={{ width: `${leftWidth}%` }}>
                    <div
                        className='gateway-tabs'
                        role='tablist'
                        aria-label={translate('ABB.Powertrain.Frontend.gatewayTitle')}
                    >
                        <button
                            className={`gateway-tab ${activeMainTab === GATEWAY_MAIN_TABS.MY_GATEWAYS ? 'gateway-tab--active' : ''}`}
                            role='tab'
                            aria-selected={activeMainTab === GATEWAY_MAIN_TABS.MY_GATEWAYS}
                            aria-controls='tabpanel-my-gateways'
                            id='tab-my-gateways'
                            tabIndex={activeMainTab === GATEWAY_MAIN_TABS.MY_GATEWAYS ? 0 : -1}
                            onClick={() => handleMainTabChange(GATEWAY_MAIN_TABS.MY_GATEWAYS)}
                            onKeyDown={(e) => {
                                if (!isGatewayReadOnlyMode && e.key === ARROW_RIGHT) {
                                    handleMainTabChange(GATEWAY_MAIN_TABS.OTHER_GATEWAYS);
                                }
                            }}
                        >
                            <span className='gateway-tab__icon'>
                                <IconBroadcast width={16} height={16} />
                            </span>
                            <span className='gateway-tab__label'>
                                {translate('ABB.Powertrain.Frontend.gatewayManagementMyGateways')}
                            </span>
                            <Tippy
                                content={translate('ABB.Powertrain.Frontend.gatewayManagementMyGatewaysInfo')}
                                interactive={true}
                                interactiveBorder={10}
                                animation='scale'
                                theme='light-border'
                                trigger='click'
                                placement='bottom'
                            >
                                <button
                                    type='button'
                                    className='gateway-tab__info'
                                    aria-label={translate('ABB.Powertrain.Frontend.gatewayManagementMyGatewaysInfo')}
                                    onClick={(e) => { e.stopPropagation(); }}
                                >
                                    <IconInfo width={'16'} height={'16'} color={colors.mineShaft} />
                                </button>
                            </Tippy>
                        </button>
                        {renderOtherGatewaysTabButton()}
                    </div>
                    {renderGatewaySubTabs()}
                    <div
                        className='gateway-management__list-and-filter'
                        role='tabpanel'
                        id={tabPanelId}
                        aria-labelledby={tabPanelAriaLabelledBy}
                    >
                        <div className='list-header'>
                            <div className='list-header__left-content' id='gateway-management__search'>
                                <div className='search-wrapper'>
                                    <SearchBox className={`search-box ${searchError ? 'search-box--error' : ''}`}
                                        placeholder={translate('ABB.Powertrain.Frontend.gatewayManagementSearch')}
                                        onChange={handleFilterSearchChange}
                                        value={searchValue}
                                    />
                                    {searchError && <div className='search-error'>
                                        <IconCancel
                                            showCircle={true}
                                            color={colors.white}
                                            fillColor={colors.pomegranate}
                                            viewBox='0 0 50 50'
                                            width={18}
                                            height={18}
                                        /><span>{searchError}</span>
                                    </div>}
                                </div>
                                <Tippy
                                    content={translate('ABB.Powertrain.Frontend.gatewaySearchMessage')}
                                    interactive={true}
                                    interactiveBorder={10}
                                    animation='scale'
                                    theme='light-border'
                                    trigger='click'
                                    placement='left'>
                                    <button type='button' className='info-icon' aria-label={translate('ABB.Powertrain.Frontend.gatewaySearchMessage')}>
                                        <IconInfo width={'16'} height={'16'} color={colors.black} />
                                    </button>
                                </Tippy>
                            </div>
                            <div className='list-header__right-content' id='gateway-management__filter-icon'>
                                <button className='refresh-icon mr-2' onClick={() => {
                                    executeSearch(searchValue);
                                }}>
                                    <IconRefresh width={'16'} height={'16'} color={colors.black} />
                                </button>
                                <button className={`filter-icon ${isFilterActive ? 'filter-icon--active' : ''}`} onClick={toggleFilter}>
                                    <IconGatewayFilter />
                                </button>
                            </div>
                        </div>
                        {isFilterActive &&
                            <div className='filter-section' id='gateway-management__filter-section'>
                                <div className='filter-section__legend'>
                                    {translate('ABB.Powertrain.Frontend.gatewayTypeLabel')}
                                </div>
                                <GeneralSelect
                                    className='gateway-type-filter'
                                    options={gatewayType?.gatewayType}
                                    getOptionLabel={option => option.gatewayTypeName}
                                    getOptionValue={option => option.gatewayType}
                                    onChange={option => {
                                        setSelectedGatewayType(option);
                                        setIsSearchPerformed(true);
                                    }}
                                    value={selectedGatewayType}
                                    menuShouldScrollIntoView={false}
                                    isSearchable={false}
                                />
                                <div className='filter-section__checkbox-btn'>
                                    <div className='filter-section__legend'>
                                        {'Connection Status'}
                                    </div>
                                    <div className='checkbox-wrapper'>
                                        <div className='checkbox-outer'>
                                            <div className='checkbox-input'>
                                                <Checkbox
                                                    checked={isConnectedChecked}
                                                    onToggle={() => {
                                                        setIsConnectedChecked(prev => !prev);
                                                        setIsSearchPerformed(true);
                                                    }}
                                                    id='checkbox-gateway__connected'
                                                />
                                            </div>
                                            <div className='label-name'>
                                                {translate('ABB.Powertrain.Frontend.gatewayStatusOnlineLabel')}
                                            </div>
                                        </div>
                                        <div className='checkbox-outer'>
                                            <div className='checkbox-input'>
                                                <Checkbox
                                                    checked={isConnectedWithWarningChecked}
                                                    onToggle={() => {
                                                        setIsConnectedWithWarningChecked(prev => !prev);
                                                        setIsSearchPerformed(true);
                                                    }}
                                                    id='checkbox-gateway__connected-with-warning'
                                                />
                                            </div>
                                            <div className='label-name'>
                                                {translate('ABB.Powertrain.Frontend.gatewayStatusOnlineWithWarningLabel')}
                                            </div>
                                        </div>
                                        <div className='checkbox-outer'>
                                            <div className='checkbox-input'>
                                                <Checkbox
                                                    checked={isDisconnectedChecked}
                                                    onToggle={() => {
                                                        setIsDisconnectedChecked(prev => !prev);
                                                        setIsSearchPerformed(true);
                                                    }}
                                                    id='checkbox-gateway__disconnected'
                                                />
                                            </div>
                                            <div className='label-name'>
                                                {translate('ABB.Powertrain.Frontend.gatewayStatusOfflinLabel')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='filter-section__btn'>
                                    <GeneralButton
                                        id='filter-section__reset'
                                        type='discreet'
                                        text={translate('ABB.Powertrain.Frontend.Reset')}
                                        onClick={handleResetFilter}
                                    />
                                    <GeneralButton
                                        id='filter-section__search'
                                        type='primary'
                                        text={translate('ABB.Powertrain.Frontend.gatewayManagementSearch')}
                                        onClick={() => handleSearch()}
                                        disabled={!isSearchPerformed}
                                    />
                                </div>
                            </div>
                        }
                        <LoadingStatusWrapper loadingStatus={gatewayDetails?.loadingStatus}>
                            {renderUnknownGatewaySection()}
                            <GatewayListManagement
                                gatewayID={gatewayID}
                                isShowGatewayDetails={isShowGatewayDetails}
                                gatewayDetails={{
                                    ...gatewayDetails,
                                    gatewayList: getActiveGatewayList()
                                }}
                                onSelectedGatewayData={handleOnSelectedGatewayData}
                            />
                        </LoadingStatusWrapper>
                    </div>
                </div>
                <div className='resize-handle'>
                    <button className='resize-handle__button' onMouseDown={handleMouseDownLeft}>
                        <IconAdjust />
                    </button>
                </div>
                <div className='gateway-management__right-pane' style={{ width: `${100 - (leftWidth + rightWidth)}%` }}>
                    <div className='right-pane__contents'>
                        {isShowGatewayDetails && gatewayExists ?
                            <GatewayDetails
                                gatewayID={gatewayID}
                                gatewayStatus={gatewayStatus}
                                statusChangeTimestamp={statusChangeTimestamp}
                                handleShowInfoClick={handleShowInfoClick}
                                onResetAssetInfo={handleResetAssetInfo}
                                currentFilters={getCurrentFilters()}
                                currentSiteIds={getCurrentSiteIds()}
                                gatewayFilter={getGatewayFilterPayload()}
                            /> : <GatewayManagementEmptyComponent />}
                    </div>
                </div>
                {enableRightPanel &&
                    <div className='resize-handle'>
                        <button className='resize-handle__button' onMouseDown={handleMouseDownRight}>
                            <IconAdjust />
                        </button>
                    </div>
                }
                {enableRightPanel && selectedDetails &&
                    <div className='gateway-management__right-panel' style={{ width: `${rightWidth}%` }}>
                        <LoadingStatusWrapper loadingStatus={saveAssetDriveApplicationLoadingStatus} transparent>
                            <div className='form-item'>
                                {renderRightPanelContent(selectedDetails)}
                            </div>
                            {canEditAsset && <div className='form_btn'>
                                <GeneralButton
                                    type='discreet'
                                    text={translate('ABB.Powertrain.Frontend.cancelLabel')}
                                    onClick={handleResetAssetInfo}
                                />
                                <GeneralButton
                                    type='primary'
                                    disabled={!hasUnsavedChanges || !validateAssetName(editedAssetName)}
                                    text={translate('ABB.Powertrain.Frontend.saveButton')}
                                    onClick={() => handleSaveAssetDriveApplication()}
                                />
                            </div>}
                        </LoadingStatusWrapper>
                    </div>
                }

            </div>
            <SerialNumberEditor
                isModalOpen={isModalOpen}
                onClose={handleCloseSerialNumberEditorModal}
                onApply={handleApplySerialNumber}
                selectedAssetDetails={selectedDetails}
                gatewayID={gatewayID}
            />
        </div>
    );
};

const IconAdjust = () => {
    return (
        <svg width='16' height='17' viewBox='0 0 16 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <circle cx='10' cy='3.5' r='1' transform='rotate(90 10 3.5)' fill='#696969' />
            <circle cx='6' cy='3.5' r='1' transform='rotate(90 6 3.5)' fill='#696969' />
            <circle cx='10' cy='8.5' r='1' transform='rotate(90 10 8.5)' fill='#696969' />
            <circle cx='6' cy='8.5' r='1' transform='rotate(90 6 8.5)' fill='#696969' />
            <circle cx='10' cy='13.5' r='1' transform='rotate(90 10 13.5)' fill='#696969' />
            <circle cx='6' cy='13.5' r='1' transform='rotate(90 6 13.5)' fill='#696969' />
        </svg>
    );
};

GatewayManagmentComponent.propTypes = {
    model: PropTypes.shape({
        selectedOrganization: PropTypes.shape({
            organizationID: PropTypes.number
        }),
        gatewayDetails: PropTypes.shape({
            loadingStatus: PropTypes.string,
            gatewayList: PropTypes.array,
            myGateways: PropTypes.array,
            otherGateways: PropTypes.shape({
                emptyGateways: PropTypes.array,
                unassignedGateways: PropTypes.array,
                unknownGateways: PropTypes.shape({
                    gatewayIdentifier: PropTypes.string,
                    connectivityStatus: PropTypes.string,
                    statusChangeTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                })
            })
        }),
        gatewayType: PropTypes.shape({
            gatewayType: PropTypes.array
        }),
        isModalOpen: PropTypes.bool,
        assignedSerialNumberLoadingStatus: PropTypes.string,
        saveAssetDriveApplicationLoadingStatus: PropTypes.string,
        gatewayAssets: PropTypes.shape({
            loadingStatus: PropTypes.string,
            gatewayConnectedAssets: PropTypes.shape({
                gatewayAssets: PropTypes.arrayOf(PropTypes.shape({
                    assetId: PropTypes.string
                }))
            })
        }),
        driveApplications: PropTypes.array
    }),
    actions: PropTypes.shape({
        getGatewayList: PropTypes.func,
        getGatewayType: PropTypes.func,
        selectAsset: PropTypes.func,
        openSerialNumberEditorModal: PropTypes.func,
        closeSerialNumberEditorModal: PropTypes.func,
        setSelectedGateway: PropTypes.func,
        getUpdateAssetDriveApplication: PropTypes.func,
        getDriveApplications: PropTypes.func
    })
};

export default GatewayManagmentComponent;
